import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import BackButton from '../ChildComponent/BackButton'
import COLORS from '../resources/colors/COLORS'
import SessionManager from '../Utils/SessionManager'
import '../css/Admin_css/admin-customize.css'
import whatsappApi from './whatsapp/whatsappApi'
import CommWhatsAppPanel from './communication/CommWhatsAppPanel'

const WA = '#25D366'
const WA_DARK = '#128C7E'
const INACTIVE_MESSAGE = 'WhatsApp Automation service is not Active on Your Account'

const CommunicationPanel = () => {
  const token = localStorage.getItem('token')
  const [loggedIn] = useState(token !== null)
  const [status, setStatus] = useState({ label: '…', ok: null, meta: '' })
  const [subscription, setSubscription] = useState(null)
  const [wallet, setWallet] = useState(null)
  const [messageServices, setMessageServices] = useState([])
  const [selectedServiceCode, setSelectedServiceCode] = useState('')
  const [statusLoading, setStatusLoading] = useState(true)
  const [serviceActive, setServiceActive] = useState(null)
  const [serviceMessage, setServiceMessage] = useState(INACTIVE_MESSAGE)
  const [accountConnected, setAccountConnected] = useState(false)

  // Created By Cursor
  const applyMessageServices = useCallback((list) => {
    const next = Array.isArray(list) ? list : []
    setMessageServices(next)
    setSelectedServiceCode((prev) => {
      if (prev && next.some((s) => String(s.service_code || s.id) === String(prev))) {
        return prev
      }
      const first = next[0]
      return first ? String(first.service_code || first.id || '') : ''
    })
  }, [])

  // Created By Cursor
  const refreshStatus = useCallback(async () => {
    setStatusLoading(true)
    try {
      const [wa, subRes, svcRes] = await Promise.all([
        whatsappApi.getAccount().catch(() => null),
        whatsappApi.getSubscription().catch(() => null),
        whatsappApi.getWhatsappSchoolMessageServices(true).catch(() => null),
      ])

      const connected =
        wa?.api_status === 'OK' &&
        wa?.data?.account?.connection_status === 'connected'
      const active =
        wa?.api_status === 'OK' ? !!wa?.data?.service_active : false
      const bal = Number(wa?.data?.wallet?.balance ?? subRes?.data?.wallet?.balance ?? 0)

      setAccountConnected(!!connected)
      setServiceActive(active)
      setServiceMessage(
        (wa?.api_status === 'OK' && wa?.data?.service_message) || INACTIVE_MESSAGE
      )

      setStatus({
        label: !active
          ? 'Not active'
          : connected
          ? wa.data.account.verified_name || 'Connected'
          : 'Not connected',
        ok: active && connected,
        meta: !active
          ? 'Service inactive'
          : connected
          ? `₹${bal.toFixed(0)}`
          : 'Setup needed',
      })

      if (wa?.api_status === 'OK' && wa?.data?.wallet) {
        setWallet(wa.data.wallet)
      } else if (subRes?.api_status === 'OK' && subRes?.data?.wallet) {
        setWallet(subRes.data.wallet)
      }

      if (subRes?.api_status === 'OK' && subRes?.data?.subscription) {
        setSubscription(subRes.data.subscription)
      } else if (wa?.api_status === 'OK' && wa?.data?.subscription) {
        setSubscription(wa.data.subscription)
      }

      if (svcRes?.api_status === 'OK' && Array.isArray(svcRes?.data?.services)) {
        applyMessageServices(svcRes.data.services)
      } else if (wa?.api_status === 'OK' && Array.isArray(wa?.data?.message_services)) {
        applyMessageServices(wa.data.message_services)
      } else {
        applyMessageServices([])
      }
    } catch (e) {
      setServiceActive(false)
      setAccountConnected(false)
      setStatus({ label: 'Not active', ok: false, meta: 'Unavailable' })
    } finally {
      setStatusLoading(false)
    }
  }, [applyMessageServices])

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!SessionManager.getSchoolCode()) return
    refreshStatus()
  }, [refreshStatus])

  const planView = useMemo(() => buildPlanView(subscription, wallet), [subscription, wallet])

  const selectedService = useMemo(() => {
    if (!selectedServiceCode) return null
    return (
      messageServices.find(
        (s) => String(s.service_code || s.id) === String(selectedServiceCode)
      ) || null
    )
  }, [messageServices, selectedServiceCode])

  // Block compose UI when WhatsApp service inactive or account not connected
  const uiBlocked = !statusLoading && (serviceActive === false || !accountConnected)
  const blockReason =
    serviceActive === false
      ? {
          title: serviceMessage || INACTIVE_MESSAGE,
          detail: 'Activate WhatsApp for this school to compose and send messages.',
          cta: 'Open WhatsApp Settings',
          tab: null,
        }
      : {
          title: 'WhatsApp account is not connected',
          detail: 'Connect and register your school WhatsApp number before sending.',
          cta: 'Connect WhatsApp',
          tab: 'connection',
        }

  if (!loggedIn) return <Redirect to="/Login" />

  return (
    <div className="content">
      <div className="content-header d-flex justify-content-between align-items-center">
        <div className="leftside-content-header d-flex align-items-center">
          <i
            className="fa fa-home"
            aria-hidden="true"
            style={{
              fontSize: 18,
              color: COLORS.primary_color,
              cursor: 'pointer',
              marginRight: 10,
            }}
            onClick={() => {
              window.location.href = '/AdminMain'
            }}
          />
          <ul className="breadcrumbs mb-0">
            <li>Communication</li>
            <li>WhatsApp</li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow"
        style={{ '--mMargin': '50px', padding: 0, overflow: 'hidden' }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '12px 16px',
            background: `linear-gradient(120deg, ${WA_DARK} 0%, ${WA} 100%)`,
            color: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: 'rgba(255,255,255,0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fa fa-whatsapp" style={{ fontSize: 18 }} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>
                WhatsApp
              </div>
              <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2 }}>
                Template messages to students
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.28)',
                borderRadius: 999,
                padding: '5px 10px',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: status.ok ? '#bbf7d0' : '#fde68a',
                }}
              />
              {status.label}
              {status.meta ? ` · ${status.meta}` : ''}
            </span>
            <HeaderNavLink to="/AdminMain/WhatsAppQueue" label="Queue" />
            <HeaderNavLink to="/AdminMain/WhatsAppInbox" label="Inbox" />
          </div>
        </div>

        <PlanUsageStrip plan={planView} />

        {statusLoading ? (
          <div
            style={{
              padding: '48px 16px',
              textAlign: 'center',
              color: '#64748b',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Checking WhatsApp account…
          </div>
        ) : uiBlocked ? (
          <AccountBlockedPanel reason={blockReason} />
        ) : (
          <>
            <MessageServicesStrip
              services={messageServices}
              selectedCode={selectedServiceCode}
              onSelect={(code) => setSelectedServiceCode(String(code || ''))}
            />
            <div style={{ padding: '14px 16px 16px' }}>
              <CommWhatsAppPanel
                compact
                onStatusChange={refreshStatus}
                selectedService={selectedService}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Created By Cursor — full-panel lock when service inactive / not connected
function AccountBlockedPanel({ reason }) {
  return (
    <div style={{ padding: '28px 16px 32px' }}>
      <div
        style={{
          maxWidth: 520,
          margin: '0 auto',
          textAlign: 'center',
          border: '1px solid #fecaca',
          borderRadius: 14,
          background: 'linear-gradient(180deg, #fef2f2 0%, #fff 70%)',
          padding: '28px 20px',
          boxShadow: '0 8px 24px rgba(185, 28, 28, 0.06)',
        }}
      >
        <span
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: '#fee2e2',
            color: '#b91c1c',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12,
          }}
        >
          <i className="fa fa-lock" style={{ fontSize: 22 }} aria-hidden />
        </span>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#991b1b', lineHeight: 1.35 }}>
          {reason?.title || INACTIVE_MESSAGE}
        </div>
        {reason?.detail ? (
          <div
            style={{
              marginTop: 8,
              fontSize: 13,
              fontWeight: 600,
              color: '#64748b',
              lineHeight: 1.45,
            }}
          >
            {reason.detail}
          </div>
        ) : null}
        <Link
          to="/AdminMain/WhatsAccountSettings"
          onClick={() => {
            if (!reason?.tab) return
            try {
              sessionStorage.setItem('wa_settings_tab', reason.tab)
            } catch (e) {
              /* ignore */
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 16,
            border: 'none',
            background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
            color: '#fff',
            fontWeight: 800,
            fontSize: 13,
            padding: '10px 16px',
            borderRadius: 8,
            textDecoration: 'none',
            boxShadow: '0 3px 10px rgba(37, 211, 102, 0.22)',
          }}
        >
          <i className="fa fa-whatsapp" aria-hidden />
          {reason?.cta || 'Open WhatsApp Settings'}
        </Link>
      </div>
    </div>
  )
}

// Created By Cursor
function HeaderNavLink({ to, label, tab }) {
  return (
    <Link
      to={to}
      onClick={() => {
        if (!tab) return
        try {
          sessionStorage.setItem('wa_settings_tab', tab)
        } catch (e) {
          /* ignore */
        }
      }}
      style={{
        color: '#fff',
        fontWeight: 700,
        fontSize: 12,
        textDecoration: 'none',
        background: 'rgba(255,255,255,0.16)',
        border: '1px solid rgba(255,255,255,0.28)',
        borderRadius: 8,
        padding: '6px 10px',
      }}
    >
      {label}
    </Link>
  )
}

// Created By Cursor
function buildPlanView(subscription, wallet) {
  if (!subscription) {
    return {
      hasPlan: false,
      name: 'No plan',
      typeLabel: '—',
      feeLabel: '—',
      used: 0,
      included: null,
      remaining: null,
      pct: null,
      inOverage: false,
      cycleEnd: null,
      overageRate: null,
      walletBalance: Number(wallet?.balance || 0),
    }
  }

  const planType = String(subscription.plan_type || 'MONTHLY').toUpperCase()
  const includedRaw = subscription.included_messages
  const included =
    includedRaw === null || includedRaw === undefined || includedRaw === ''
      ? null
      : Number(includedRaw)
  const used = Number(subscription.messages_used_this_cycle || 0)
  const remaining =
    subscription.messages_remaining === null ||
    subscription.messages_remaining === undefined
      ? included === null
        ? null
        : Math.max(0, included - used)
      : Number(subscription.messages_remaining)
  const pct =
    subscription.usage_percent === null || subscription.usage_percent === undefined
      ? included && included > 0
        ? Math.min(100, Math.round((used / included) * 1000) / 10)
        : null
      : Number(subscription.usage_percent)
  const inOverage = !!subscription.in_overage || (included !== null && used >= included)

  return {
    hasPlan: true,
    name: subscription.plan_name || subscription.plan_id || 'Plan',
    planId: subscription.plan_id || '',
    typeLabel: planType === 'USAGE_BASED' ? 'Usage based' : 'Monthly',
    feeLabel: `₹${Number(subscription.monthly_fee || 0).toFixed(0)}/mo`,
    used,
    included,
    remaining,
    pct,
    inOverage,
    cycleEnd: subscription.cycle_end_date || null,
    overageRate:
      subscription.overage_price_per_message != null
        ? Number(subscription.overage_price_per_message)
        : null,
    walletBalance: Number(wallet?.balance || 0),
    status: subscription.status || 'active',
  }
}

// Created By Cursor
function PlanUsageStrip({ plan }) {
  if (!plan?.hasPlan) {
    return (
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid #e2e8f0',
          background: '#fffbeb',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
          No WhatsApp subscription assigned yet
        </span>
        <Link
          to="/AdminMain/WhatsAccountSettings"
          onClick={() => {
            try {
              sessionStorage.setItem('wa_settings_tab', 'subscription')
            } catch (e) {
              /* ignore */
            }
          }}
          style={{ fontSize: 12, fontWeight: 700, color: WA_DARK }}
        >
          Choose a plan →
        </Link>
      </div>
    )
  }

  const barPct = plan.pct != null ? Math.min(100, Math.max(0, plan.pct)) : null

  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e2e8f0',
        background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
          marginBottom: plan.included !== null ? 10 : 0,
        }}
      >
        <MetaCell label="Plan" value={plan.name} sub={plan.planId || plan.typeLabel} />
        <MetaCell label="Type" value={plan.typeLabel} sub={plan.feeLabel} />
        <MetaCell
          label="Usage"
          value={
            plan.included === null
              ? `${plan.used.toLocaleString('en-IN')} sent`
              : `${plan.used.toLocaleString('en-IN')} / ${plan.included.toLocaleString('en-IN')}`
          }
          sub={
            plan.included === null
              ? plan.cycleEnd
                ? `Resets ${plan.cycleEnd}`
                : 'This cycle'
              : `${(plan.remaining ?? 0).toLocaleString('en-IN')} left`
          }
        />
        <MetaCell
          label={plan.inOverage ? 'Overage wallet' : 'Wallet'}
          value={`₹${plan.walletBalance.toFixed(0)}`}
          sub={
            plan.overageRate != null
              ? `₹${plan.overageRate.toFixed(4)}/msg overage`
              : plan.inOverage
              ? 'In overage'
              : 'Within allowance'
          }
          tone={plan.inOverage ? 'warn' : 'ok'}
        />
      </div>

      {barPct !== null && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              fontWeight: 700,
              color: '#64748b',
              marginBottom: 4,
            }}
          >
            <span>Cycle usage</span>
            <span>{barPct}%</span>
          </div>
          <div
            style={{
              height: 8,
              borderRadius: 999,
              background: '#e2e8f0',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${barPct}%`,
                height: '100%',
                background: plan.inOverage
                  ? 'linear-gradient(90deg, #f59e0b, #ef4444)'
                  : `linear-gradient(90deg, ${WA_DARK}, ${WA})`,
                transition: 'width 0.25s ease',
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// Created By Cursor
function MessageServicesStrip({ services, selectedCode, onSelect }) {
  const list = Array.isArray(services) ? services : []

  if (list.length === 0) {
    return (
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            background: '#fff',
            padding: '12px 14px',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
            No messaging services enabled for this school
          </span>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
      }}
    >
      <div
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          background: '#fff',
          padding: '14px 14px 12px',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
            paddingBottom: 10,
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className="fa fa-check" style={{ fontSize: 11 }} aria-hidden />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: '#0f172a',
                lineHeight: 1.2,
              }}
            >
              Enabled message services
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginTop: 2 }}>
              {list.length} active · select one to compose
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="tablist">
          {list.map((svc) => {
            const key = String(svc.service_code || svc.id || '')
            const name = svc.service_name || svc.service_code || 'Service'
            const isSelected = String(selectedCode) === key
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={isSelected}
                title={svc.description || name}
                onClick={() => onSelect && onSelect(key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: isSelected
                    ? `linear-gradient(135deg, ${WA_DARK}, ${WA})`
                    : '#f8fafc',
                  border: isSelected ? `1px solid ${WA_DARK}` : '1px solid #e2e8f0',
                  color: isSelected ? '#fff' : '#0f172a',
                  borderRadius: 10,
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: isSelected
                    ? '0 4px 12px rgba(18, 140, 126, 0.28)'
                    : 'none',
                  outline: 'none',
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: isSelected ? 'rgba(255,255,255,0.22)' : `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i
                    className={`fa ${svc.icon || 'fa-comment'}`}
                    style={{ fontSize: 12 }}
                    aria-hidden
                  />
                </span>
                {name}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// Created By Cursor
function MetaCell({ label, value, sub, tone }) {
  const valueColor =
    tone === 'warn' ? '#b45309' : tone === 'ok' ? WA_DARK : '#0f172a'
  return (
    <div
      style={{
        minWidth: 0,
        padding: '8px 10px',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
        background: '#fff',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: '#94a3b8',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: valueColor,
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={String(value)}
      >
        {value}
      </div>
      {sub ? (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontWeight: 600 }}>
          {sub}
        </div>
      ) : null}
    </div>
  )
}

export default CommunicationPanel
